import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormHelperText,
  Select,
  MenuItem,
  Button,
  Autocomplete,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import { BglPrimaryButton } from 'components/button/BglButtons';
import { useGlobalConfig } from 'context/GlobalContext';
import { ROUTE_TREE } from 'routes/AppRoutes';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LayersIcon from '@mui/icons-material/Layers';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PilotApis, ErrorHandler } from 'apis/pilotAPI';
import { useToast } from '../../context/ToastContext';
import { ConfigRegistryType } from 'types/apis/pilotInterfaces';
import {
  createProjectPartOneSchema,
  type CreateProjectPartOneValues,
  awsRegionOptions,
} from 'types/validators/project-validators';
import { useDispatch, useSelector } from 'react-redux';
import { 
  setConfigRegistryData, 
  clearConfigRegistryData,
  selectConfigRegistryData
} from 'store/dataReducers/configRegistrySlice';
import { FIRST_STEP_CONFIG_MAPPING } from './configMapping';
import { DynamicConfigForm } from './DynamicConfigForm';
import { filterConfigsNoDuplicates, isDuplicateConfig } from './registryUtils';
import { filterRegistryByComponents } from './registryUtils';
import EditDocumentIcon from '@mui/icons-material/EditDocument';
import { useTheme } from '@mui/material/styles';
import { Package, Save } from 'lucide-react';
import { FilePenLine } from 'lucide-react';



// Constants for session storage keys
export const FORM_PART_ONE_KEY = 'create-project-form-part-one';
export const FORM_PART_TWO_KEY = 'create-project-form-part-two';
export const EDIT_PROJECT_DATA_KEY = 'edit-project-data';
export const ENVIRONMENT_CREATION_DATA_KEY = 'environment_creation_data';
export const FORM_PART_ONE_CONFIGS_KEY = 'create-project-configs';



export const UtilityCreationPage = () => {
  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [isEditMode, setIsEditMode] = useState(false);
  const [stepOneData, setStepOneData] = useState<any>(null);
  const [utilities, setUtilities] = useState<any[]>([]);
  const [isLoadingUtilities, setIsLoadingUtilities] = useState(false);
  const [utilitySearchTerm, setUtilitySearchTerm] = useState('');
  const [isCreatingUtility, setIsCreatingUtility] = useState(false);
  const [newUtilityName, setNewUtilityName] = useState('');
  const [showCreateUtilityDialog, setShowCreateUtilityDialog] = useState(false);
  const [createUtilityError, setCreateUtilityError] = useState<string>('');
  const [environmentSetupType, setEnvironmentSetupType] = useState<'existing' | 'new'>('new');
  const [environments, setEnvironments] = useState<any[]>([]);
  const [isLoadingEnvironments, setIsLoadingEnvironments] = useState(false);
  const [projectAlreadyExists, setProjectAlreadyExists] = useState(false);


  
  // Step 3: Create filtered state for config registry data
  const [filteredConfigRegistryData, setFilteredConfigRegistryData] = useState<any[]>([]);
  
  const dispatch = useDispatch();
  const configRegistryData = useSelector(selectConfigRegistryData);

  const { control, handleSubmit, watch, setValue, getValues, trigger, formState: { errors } } = useForm<CreateProjectPartOneValues>({
    resolver: zodResolver(createProjectPartOneSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      environment_id: undefined, // For existing environment
      environment_name: '', // For new environment
      awsRegion: 'us-west-1',
      country: undefined,
      projectId: "",
      name: "",
      description: "",
      projectImageUrl: "",
      languages: undefined,
      timeZone: undefined,
      utility: undefined,
    },
  });

  const utilityValue = watch("utility");

  // Function to fetch utilities from API
  const fetchUtilities = async () => {
    setIsLoadingUtilities(true);
    try {
      const response = await PilotApis.getUtilities(rc);
      if (response.error) {
        console.error('Error fetching utilities:', response.error);
        setUtilities([]);
      } else {
        const utilitiesData = response.data?.data || [];
        setUtilities(utilitiesData);
      }
    } catch (error) {
      console.error('Exception fetching utilities:', error);
      setUtilities([]);
    } finally {
      setIsLoadingUtilities(false);
    }
  };

   // Function to fetch environments for a utility
   const fetchEnvironments = async (utilityId: number) => {
    setIsLoadingEnvironments(true);
    try {
      const response = await PilotApis.getEnvironments(rc, utilityId);
      if (response.error) {
        console.error('Error fetching environments:', response.error);
        setEnvironments([]);
      } else {
        const environmentsData = response.data?.data || [];
        setEnvironments(environmentsData);
      }
    } catch (error) {
      console.error('Exception fetching environments:', error);
      setEnvironments([]);
    } finally {
      setIsLoadingEnvironments(false);
    }
  };

  // Function to create a new utility
  const createNewUtility = async (utilityName: string) => {
    setIsCreatingUtility(true);
    setCreateUtilityError('');
    try {
      const response = await PilotApis.createUtility(rc, { name: utilityName });
      if (response.error) {
        console.error('Error creating utility:', response.error);
        setCreateUtilityError(response.error?.message || 'Failed to create utility. Please try again.');
        return null;
      } else {
        const responseData = response.data;
        if (responseData.success && responseData.data) {
          const newUtility = responseData.data;
          toast.success(responseData.message || 'Utility created successfully!');
          setUtilities(prev => [...prev, newUtility]);
          setValue('utility', newUtility);
          setShowCreateUtilityDialog(false);
          setNewUtilityName('');
          return newUtility;
        } else {
          setCreateUtilityError('Failed to create utility. Invalid response format.');
          return null;
        }
      }
    } catch (error) {
      console.error('Exception creating utility:', error);
      setCreateUtilityError('Failed to create utility. Please try again.');
      return null;
    } finally {
      setIsCreatingUtility(false);
    }
  };

  // Function to handle create utility dialog
  const handleCreateUtilityClick = () => {
    setShowCreateUtilityDialog(true);
    setCreateUtilityError('');
  };

  // Function to handle create utility submission
  const handleCreateUtilitySubmit = () => {
    if (!newUtilityName.trim()) {
      setCreateUtilityError('Utility name is required');
      return;
    }
    
    // Check if utility name already exists
    const trimmedName = newUtilityName.trim();
    const existingUtility = utilities.find(utility => 
      utility.name.toLowerCase() === trimmedName.toLowerCase()
    );
    
    if (existingUtility) {
      setCreateUtilityError(`A utility with the name "${trimmedName}" already exists. Please choose a different name.`);
      return;
    }
    
    createNewUtility(trimmedName);
  };

  // Effect to fetch utilities when search term changes
  useEffect(() => {
    fetchUtilities();
  }, [rc]);

  useEffect(() => {
    if (utilityValue && utilityValue.id) {
      fetchEnvironments(utilityValue.id);
    } else {
      setEnvironments([]);
    }
  }, [utilityValue]);


  // Step 1 & 2: Load config registry data and set to Redux
  useEffect(() => {
    const loadConfigRegistry = async () => {
      try {
        const response = await PilotApis.getConfigRegistry(rc);
        if (response.success && response.data) {
          // Step 2: Set the response to Redux for further usage
          dispatch(setConfigRegistryData(response.data));
          console.log('Config registry data loaded and set to Redux:', response.data);
        } else {
          console.error('Failed to load config registry:', response.error);
        }
      } catch (err) {
        console.error('Error loading config registry:', err);
      }
    };

    // Only load if config registry data is empty
    if (configRegistryData.length === 0) {
      loadConfigRegistry();
    }
  }, [rc, dispatch, configRegistryData.length]);

  // Step 3: Create filtered state with utilityRequired=true & configClassification=METADATA
  // Step 4: Populate the form with the filtered data
  useEffect(() => {
    if (configRegistryData.length > 0) {
      // First restrict by allowed components, then filter by METADATA required and remove duplicates
      const componentFiltered = filterRegistryByComponents(configRegistryData);
      const filtered = filterConfigsNoDuplicates(
        componentFiltered,
        (config: any) => config.utilityConfigurationRequired && config.configClassification === 'METADATA'
      );
      
      console.log('Filtered config registry data (with duplicate handling):', filtered);
      setFilteredConfigRegistryData(filtered);
      
      // Step 5: Populate form with default values from registry
      filtered.forEach((configType: ConfigRegistryType) => {
        configType.configs.forEach((config: any) => {
          const fieldName = `${configType.configType}.${config.configKey}`;
          let defaultValue: any = '';
          
          if (config.configDataType === 'BOOLEAN') {
            defaultValue = config.defaultValue === 'true' || config.defaultValue === 'TRUE';
          } else if (config.defaultValue) {
            defaultValue = String(config.defaultValue);
          } else {
            defaultValue = '';
          }
          
          console.log(`Setting default for ${fieldName}:`, defaultValue);
          // Set the nested structure correctly
          (setValue as any)(fieldName, defaultValue);
        });
      });
    }
  }, [configRegistryData, setValue]);

  // Create filtered data for DynamicConfigForm (exclude mapped configs)
  const dynamicConfigData = React.useMemo(() => {
    if (filteredConfigRegistryData.length === 0) return [];
    
    return filteredConfigRegistryData
      .map(configType => ({
        ...configType,
        configs: configType.configs.filter((config: any) => 
          !FIRST_STEP_CONFIG_MAPPING.some(mapping => mapping.configKey === config.configKey)
        )
      }))
      .filter(configType => configType.configs.length > 0);
  }, [filteredConfigRegistryData]);

  useEffect(() => {
    // Check if we're in edit mode by looking for EDIT_PROJECT_DATA_KEY first
    const editData = sessionStorage.getItem(EDIT_PROJECT_DATA_KEY);
    if (editData) {
      try {
        // Clean up form data when in edit mode
        sessionStorage.removeItem(FORM_PART_ONE_KEY);
        const parsedEditData = JSON.parse(editData);

        setStepOneData(parsedEditData);
        
        // Log the loaded data for debugging
        console.log('Loaded edit data:', parsedEditData);
        console.log('ProjectId from edit data:', parsedEditData.projectId);
        
        // Only set edit mode if there's a projectId in the data
        if (parsedEditData.projectId) {
          setIsEditMode(true);
          console.log('Set to edit mode with projectId:', parsedEditData.projectId);
        } else {
          setIsEditMode(false);
          console.log('Set to create mode - no projectId found');
        }
        
        // Prefill form data if available
        if (parsedEditData.environment_id) {
          setValue("environment_id", parsedEditData.environment_id);
        }
        if (parsedEditData.environment_name) {
          setValue("environment_name", parsedEditData.environment_name);
        }
        if (parsedEditData.awsRegion) {
          setValue("awsRegion", parsedEditData.awsRegion);
        }
        if (parsedEditData.country) {
          setValue("country", parsedEditData.country);
        }
        if (parsedEditData.projectId) {
          setValue("projectId", parsedEditData.projectId);
        }
        if (parsedEditData.name) {
          setValue("name", parsedEditData.name);
        }
        if (parsedEditData.description) {
          setValue("description", parsedEditData.description);
        }
        if (parsedEditData.projectImageUrl) {
          setValue("projectImageUrl", parsedEditData.projectImageUrl);
        }
        if (parsedEditData.languages) {
          setValue("languages", parsedEditData.languages);
        }
        if (parsedEditData.timeZone) {
          setValue("timeZone", parsedEditData.timeZone);
        }
        if (parsedEditData.utility) {
          setValue("utility", parsedEditData.utility);
        }
        if (parsedEditData.projectType) {
          setValue("projectType", parsedEditData.projectType);
        }
        
        // Ensure projectId is preserved
        const projectId = parsedEditData.projectId;
        if (projectId) {
          console.log('Restored projectId in edit mode:', projectId);
        }
      } catch (err) {
        console.error('Error loading edit data:', err);
        setError('Failed to load project data for editing');
      }
    } else {
      // Load step 1 data from sessionStorage for create mode
      const storedData = sessionStorage.getItem(FORM_PART_ONE_KEY);
      if (storedData) {
        try {
          const parsedData = JSON.parse(storedData);
          setStepOneData(parsedData);
          
          // Log the loaded data for debugging
          console.log('Loaded create data:', parsedData);
          console.log('ProjectId from create data:', parsedData.projectId);
          
          // For create mode, we should have FORM_PART_ONE_KEY but not EDIT_PROJECT_DATA_KEY
          // If we have FORM_PART_ONE_KEY, it's always create mode (even if projectId exists)
          setIsEditMode(false);
          
          // Check if project already exists
          if (parsedData.projectId) {
            setProjectAlreadyExists(true);
            console.log('Project already exists with projectId:', parsedData.projectId);
          } else {
            setProjectAlreadyExists(false);
            console.log('No existing project found');
          }
          
          console.log('Set to create mode - using FORM_PART_ONE_KEY');
          
          // Prefill form data if available (for both edit and create modes)
          if (parsedData.environment_id) {
            setValue("environment_id", parsedData.environment_id);
          }
          if (parsedData.environment_name) {
            setValue("environment_name", parsedData.environment_name);
          }
          if (parsedData.awsRegion) {
            setValue("awsRegion", parsedData.awsRegion);
          }
          if (parsedData.country) {
            setValue("country", parsedData.country);
          }
          if (parsedData.projectId) {
            setValue("projectId", parsedData.projectId);
          }
          if (parsedData.name) {
            setValue("name", parsedData.name);
          }
          if (parsedData.description) {
            setValue("description", parsedData.description);
          }
          if (parsedData.projectImageUrl) {
            setValue("projectImageUrl", parsedData.projectImageUrl);
          }
          if (parsedData.languages) {
            setValue("languages", parsedData.languages);
          }
          if (parsedData.timeZone) {
            setValue("timeZone", parsedData.timeZone);
          }
          if (parsedData.utility) {
            setValue("utility", parsedData.utility);
          }
          if (parsedData.projectType) {
            setValue("projectType", parsedData.projectType);
          }
          
          // Ensure projectId is preserved
          const projectId = parsedData.projectId;
          if (projectId) {
            console.log('Restored projectId in create mode:', projectId);
          }
        } catch (err) {
          // If data is corrupted, clear it and start fresh
          sessionStorage.removeItem(FORM_PART_ONE_KEY);
          dispatch(clearConfigRegistryData());
        }
      } else {
        // If no data, start fresh (this is the normal case for new project creation)
        setIsEditMode(false);
      }
    }
  }, [rc.uiRouter, setValue]);

  // Effect to handle utility restoration after utilities are loaded
  useEffect(() => {
    if (utilities.length > 0 && stepOneData) {
      // If we have utilities loaded and step one data, try to restore the utility
      if (stepOneData.utility_id && !stepOneData.utility) {
        // Find the utility by ID
        const foundUtility = utilities.find(u => u.id === stepOneData.utility_id);
        if (foundUtility) {
          setValue("utility", foundUtility);
        }
      } else if (stepOneData.utility) {
        // If we have a utility object, make sure it's properly set
        // Try to find the utility in the loaded utilities to ensure it matches
        const foundUtility = utilities.find(u => u.id === stepOneData.utility.id);
        if (foundUtility) {
          setValue("utility", foundUtility);
        } else {
          setValue("utility", stepOneData.utility);
        }
      }
    }
  }, [utilities, stepOneData, setValue]);

  const generateProjectId = () => {
    const randomId = Math.floor(10000 + Math.random() * 90000).toString();
    setValue('projectId', randomId);
  };



  const onSubmit = async (values: CreateProjectPartOneValues) => {
    // Validate all fields including dynamic configs before proceeding
    const isValid = await trigger(undefined, { shouldFocus: true } as any);
    if (!isValid) {
      toast.error('Please fill all required fields.');
      return;
    }

    setIsLoading(true);
    setError(undefined);

    try {
      // Transform utility object to utility_id for API, but preserve utility object for session storage
      const transformedValues = {
        ...values,
        utility_id: values.utility?.id,
        utility: undefined, // Remove the full utility object for API
        // Force backend region to us-west-1 regardless of UI selection
        awsRegion: 'us-west-1',
        // Handle environment data based on setup type
        environment_id: environmentSetupType === 'existing' ? values.environment_id : undefined,
        environment_name: environmentSetupType === 'new' ? values.environment_name : undefined,
      };
      
      // Create a version with utility object preserved for session storage
      const sessionStorageValues = {
        ...values,
        utility_id: values.utility?.id,
        // Keep the utility object for session storage
      };
      
      if (isEditMode) {
        // Edit mode - update existing project
        const projectId = stepOneData.projectId;
        if (projectId) {
          try {
            console.log('Updating project with projectId:', projectId, 'and data:', transformedValues);
            const updateResponse = await PilotApis.updatePilot(rc, projectId, transformedValues);
            
            if (updateResponse.error) {
              const errorMessage = ErrorHandler.handleProjectError(updateResponse.error, 'update');
              setError(errorMessage);
              return;
            }
            
            // Check if project update was successful
            if (updateResponse.data || updateResponse.success) {
              console.log('Project updated successfully with projectId:', projectId);
              
              // No need to save configs in edit mode - only updating basic project details
            } else {
              console.error('Project update response indicates failure:', updateResponse);
              const errorMessage = ErrorHandler.handleProjectError('Project update failed - no success response', 'update');
              setError(errorMessage);
              return;
            }
            
            // Store updated data with project ID
            let edit_data = JSON.parse(sessionStorage.getItem(EDIT_PROJECT_DATA_KEY) || '{}');
            let finalValues = {
              ...edit_data,
              ...transformedValues,
              projectId: projectId,
              isEditMode: true, // Flag to indicate this is edit mode
            };
            sessionStorage.setItem(EDIT_PROJECT_DATA_KEY, JSON.stringify(finalValues));
            
            toast.success('Project updated successfully!');
          } catch (updateError) {
            const errorMessage = ErrorHandler.handleProjectError(updateError, 'update');
            setError(errorMessage);
            return;
          }
        } else {
          // No project ID, store data as before
          let edit_data = JSON.parse(sessionStorage.getItem(EDIT_PROJECT_DATA_KEY) || '{}');
          let finalValues = {
            ...edit_data,
            ...transformedValues,
            isEditMode: true, // Flag to indicate this is edit mode
          };
          sessionStorage.setItem(EDIT_PROJECT_DATA_KEY, JSON.stringify(finalValues));
        }
        
        // For edit mode, redirect to project list after completion
        rc.uiRouter.navigate(ROUTE_TREE.PROJECT.LIST);
        return;
      } else {
        // Create mode - store step data and configs in session only, no API calls here
        try {
          sessionStorage.setItem(FORM_PART_ONE_KEY, JSON.stringify(sessionStorageValues));
          // Save all current form values (including dynamic configs shown on step 1) under a dedicated configs key
          sessionStorage.setItem(FORM_PART_ONE_CONFIGS_KEY, JSON.stringify(getValues()));
        } catch (e) {
          console.error('Failed to save step 1 data to session storage:', e);
        }
      }
      toast.success("Core Attributes Saved: Basic project information has been saved. Proceeding to the next step.");
      
      // Navigate to step 2
      rc.uiRouter.navigate(ROUTE_TREE.PROJECT.CREATE_DETAILS);
    } catch (err) {
      const errorMessage = ErrorHandler.handleProjectError(err, 'create');
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.default',
    }}>
      <Box sx={{ 
        p: 3,
      }}>
        <Box sx={{ p:4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h4" sx={{ 
                  fontSize: '1.8rem', 
                  display: 'flex', 
                  alignItems: 'center'
                }}>
                 {!isEditMode && <Package style={{ marginRight: 16, color: `${theme.palette.primary.main}` }} size={34} />}
                  {isEditMode ? "Edit Project Setup" : "Create New Project"}
            </Typography>
            {!isEditMode && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Step 1 of 2
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ maxWidth: '800px', mx: 'auto'}}>
          <Card sx={{ bgcolor: 'background.paper', boxShadow: 4, borderRadius: 2 }}>
            <Box sx={{ pl: 3,pt:2,pb:0}}>
              <Typography variant="h4" sx={{ 
                fontSize: '1.6rem',
                display: 'flex', 
                alignItems: 'center',
                mb: 1
              }}>
                
                <FilePenLine style={{ marginRight: 16, color: `${theme.palette.primary.main}` }} size={30} />
                Core Attributes
                {/* {isEditMode ? 'Update Project Details' : 
                 projectAlreadyExists ? 'Update Project Details' : 'Create New Project'} */}
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1rem', color: 'text.secondary',textAlign:"left" }}>
                {isEditMode ? 'Update the project details below.' : 
                 projectAlreadyExists ? 'Update the project details below and continue to step 2.' : 'Fill in the core details below to start a new project.'}
              </Typography>
            </Box>
            
            <form onSubmit={handleSubmit(onSubmit)}>
              <CardContent sx={{ p: 4, pt: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Error Display */}
                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  {/* Project Type */}
                  <Controller
                      name="projectType"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.projectType}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
                              Project Type
                              <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
                            </Typography>
                            <Tooltip title="Classify the project based on its purpose." arrow placement="top">
                              <IconButton size="small" sx={{ p: 0.25 }}>
                                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Select
                            {...field}
                            value={field.value || ''}
                            onChange={(e) => field.onChange(e.target.value)}
                            displayEmpty
                            sx={{ '& .MuiSelect-select': { textAlign: 'left', fontFamily: 'inherit' } }}
                          >
                            <MenuItem value="" disabled sx={{ textAlign: 'left', fontFamily: 'inherit' }}>
                              Select an option
                            </MenuItem>
                            {['ROLLOUT','POC','INTERNAL','DEMO'].map(opt => (
                              <MenuItem key={opt} value={opt} sx={{ textAlign: 'left', fontFamily: 'inherit' }}>{opt}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText error={!!errors.projectType} sx={{ textAlign: 'left', fontFamily: 'inherit' }}>
                            {(errors as any)?.projectType?.message || ''}
                          </FormHelperText>
                        </FormControl>
                      )}
                    />

                  {/* Utility Selection */}
                  <Controller
                      name="utility"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.utility}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                            <Typography variant="subtitle1" sx={{ 
                              display: 'flex', 
                              alignItems: 'center' 
                            }}>
                              {/* <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} /> */}
                              Utility
                              <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
                            </Typography>
                            <Tooltip title="Select the utility for this project or create a new one. The utility represents the energy company or organization that owns the customer data." arrow placement="top">
                              <IconButton size="small" sx={{ p: 0.25 }}>
                                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Autocomplete
                            options={[
                              { id: 'create-new', name: 'Create new utility...', isCreateNew: true },
                              ...utilities
                            ]}
                            getOptionLabel={(option) => option.name}
                            value={field.value || null}
                            onChange={(event, newValue) => {
                              if (newValue?.isCreateNew) {
                                // Show create utility dialog
                                handleCreateUtilityClick();
                                return;
                              }
                              field.onChange(newValue);
                            }}
                            onInputChange={(event, newInputValue) => {
                              setUtilitySearchTerm(newInputValue);
                            }}
                            loading={isLoadingUtilities || isCreatingUtility}
                            filterOptions={(x) => x} // Disable built-in filtering
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select a utility..."
                                size="small"
                                error={!!errors.utility}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {(isLoadingUtilities || isCreatingUtility) ? <CircularProgress color="inherit" size={20} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            renderOption={(props, option) => (
                              <Box component="li" {...props}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {option.isCreateNew ? (
                                    <AddIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                  ) : option.logoWeb?.url ? (
                                    <img 
                                      src={option.logoWeb.url} 
                                      alt={option.name}
                                      style={{ height: 20, width: 30, objectFit: 'contain' }}
                                    />
                                  ) : null}
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: option.isCreateNew ? 'primary.main' : 'inherit',
                                      fontWeight: option.isCreateNew ? 600 : 400
                                    }}
                                  >
                                    {option.name}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                            noOptionsText={
                              utilitySearchTerm.length < 2 
                                ? "Type at least 2 characters to search utilities"
                                : "No utilities found"
                            }
                          />
                          <FormHelperText error={!!errors.utility}>
                            {errors.utility?.message || ''}
                          </FormHelperText>
                        </FormControl>
                      )}
                    />

                  {/* Environment Setup */}
                  {utilityValue && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body1" sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        // mb: 2 
                      }}>
                        Environment Setup
                      </Typography>
                      
                      <FormControl component="fieldset">
                        <RadioGroup
                          value={environmentSetupType}
                          onChange={(e) => {
                            const value = e.target.value as 'existing' | 'new';
                            setEnvironmentSetupType(value);
                            setValue('environment_id', null, { shouldValidate: true });
                            setValue('environment_name', '', { shouldValidate: true });
                            trigger(['environment_id','environment_name']);
                          }}
                          row
                        >
                          <FormControlLabel
                            value="existing"
                            control={<Radio />}
                            label="Use Existing Environment"
                            sx={{ textAlign: 'left' }}
                          />
                          <FormControlLabel
                            value="new"
                            control={<Radio />}
                            label="Create New Environment"
                            sx={{ textAlign: 'left' }}
                          />
                        </RadioGroup>
                      </FormControl>

                      {/* Existing Environment Section */}
                      {environmentSetupType === 'existing' && (
                        <Box sx={{ ml: 4, borderLeft: 1, borderLeftStyle: "dashed", borderLeftColor: theme.palette.secondary.light, pl: 2  }}>
                          <FormControl fullWidth error={!!errors.environment_id}>
                            <Typography variant="subtitle1" sx={{ mb: 1, textAlign: "left", fontFamily: 'inherit' }}>
                              Existing Environment
                            </Typography>
                            <Controller
                              name="environment_id"
                              control={control}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  value={field.value || ''}
                                  onChange={(e) => {
                                    const selectedEnvironment = environments.find(env => env.id === (e.target.value as any));
                                    const nextValue = selectedEnvironment ? selectedEnvironment.id : '';
                                    field.onChange(nextValue);
                                    if (selectedEnvironment) {
                                      setValue("environment_name", "", { shouldValidate: true });
                                      console.log('Selected existing environment ID:', selectedEnvironment.id);
                                    }
                                    trigger(['environment_id','environment_name']);
                                  }}
                                  displayEmpty
                                  disabled={isLoadingEnvironments}
                                  sx={{
                                    '& .MuiSelect-select': {
                                      textAlign: 'left',
                                      fontFamily: 'inherit',
                                    },
                                  }}
                                >
                                  <MenuItem value="" disabled sx={{ textAlign: 'left', fontFamily: 'inherit' }}>
                                    {isLoadingEnvironments ? 'Loading environments...' : 'Select an option'}
                                  </MenuItem>
                                  {environments.map((environment) => (
                                    <MenuItem key={environment.id} value={environment.id} sx={{ textAlign: 'left', fontFamily: 'inherit' }}>
                                      {environment.environment_name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            />
                              <FormHelperText error={!!errors.environment_id} sx={{ textAlign: 'left', fontFamily: 'inherit' }}>
                                {errors.environment_id?.message || ''}
                              </FormHelperText>
                            </FormControl>
                        </Box>
                      )}

                      {/* New Environment Section */}
                      {environmentSetupType === 'new' && (
                        <Box sx={{ ml: 4, borderLeft: 1, borderLeftStyle: "dashed", borderLeftColor: theme.palette.secondary.light, pl: 2 }}>
                          <Controller
                            name="environment_name"
                            control={control}
                            render={({ field }) => (
                              <FormControl fullWidth error={!!errors.environment_name || !!errors.environment_id}>
                                <Typography variant="subtitle1" sx={{ mb: 1, textAlign: "left", fontFamily: 'inherit' }}>
                                  New Environment Name
                                </Typography>
                              <TextField
                                {...field}
                                placeholder="e.g., Q4-UAT"
                                size="small"
                                error={!!errors.environment_name || !!errors.environment_id}
                                onChange={(e) => {
                                  // Allow users to type freely, validation will happen on blur/submit
                                  field.onChange(e.target.value);
                                }}
                                onBlur={(e) => {
                                  // Clean up the value when user finishes typing
                                  let value = e.target.value;
                                  
                                  // Only allow alphanumeric characters and hyphens
                                  value = value.replace(/[^a-zA-Z0-9-]/g, '');
                                  
                                  // Prevent consecutive hyphens
                                  value = value.replace(/--+/g, '-');
                                  
                                  // Prevent starting with hyphen
                                  value = value.replace(/^-+/, '');
                                  
                                  // Prevent ending with hyphen
                                  value = value.replace(/-+$/, '');
                                  
                                  // Update the field with cleaned value
                                  field.onChange(value);
                                  setValue("environment_id", null, { shouldValidate: true }); // Clear existing environment ID
                                  trigger(['environment_id','environment_name']);
                                }}
                                inputProps={{
                                  pattern: '[a-zA-Z0-9-]*',
                                  title: 'Only letters, numbers, and hyphens are allowed'
                                }}
                              />
                                <FormHelperText error={!!errors.environment_name || !!errors.environment_id}>
                                  {errors.environment_name?.message || errors.environment_id?.message || ''}
                                </FormHelperText>
                              </FormControl>
                            )}
                          />
                        </Box>
                      )}
                    </Box>
                  )}

                     {(utilityValue || isEditMode || stepOneData || getValues("projectId") || getValues("name") || getValues("description")) && (
                       <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                         {/* AWS Region */}
                         <Controller
                           name="awsRegion"
                           control={control}
                           render={({ field }) => (
                             <FormControl fullWidth>
                               <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                                 <Typography variant="subtitle1" sx={{ 
                                   display: 'flex', 
                                   alignItems: 'center' 
                                 }}>
                                   {/* <LocationOnIcon sx={{ mr: 1, color: 'primary.main' }} /> */}
                                   AWS Region
                                 </Typography>
                                 <Tooltip title="The Amazon Web Services region where your project infrastructure will be deployed. Currently set to us-west-1 for all projects." arrow placement="top">
                                   <IconButton size="small" sx={{ p: 0.25 }}>
                                     <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                   </IconButton>
                                 </Tooltip>
                               </Box>
                               <Select
                                 {...field}
                                 value={field.value || ''}
                                 onChange={(e) => field.onChange(e.target.value)}
                                 displayEmpty
                                 sx={{ '& .MuiSelect-select': { textAlign: 'left', fontFamily: 'inherit' } }}
                               >
                                 <MenuItem value="" disabled sx={{ textAlign: 'left', fontFamily: 'inherit' }}>
                                   Select an option
                                 </MenuItem>
                                 {awsRegionOptions.map(option => (
                                   <MenuItem key={option.value} value={option.value} sx={{ textAlign: 'left', fontFamily: 'inherit' }}>
                                     {option.label}
                                   </MenuItem>
                                 ))}
                               </Select>
                               <FormHelperText error={!!errors.awsRegion} sx={{ textAlign: 'left', fontFamily: 'inherit' }}>
                                 {(errors as any)?.awsRegion?.message || ''}
                               </FormHelperText>
                             </FormControl>
                           )}
                         />

                         {/* Project ID */}
                         <Controller
                           name="projectId"
                           control={control}
                           render={({ field }) => (
                             <FormControl fullWidth error={!!errors.projectId}>
                               <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                                 <Typography variant="subtitle1" sx={{ 
                                   display: 'flex', 
                                   alignItems: 'center' 
                                 }}>
                                   {/* <FingerprintIcon sx={{ mr: 1, color: 'primary.main' }} /> */}
                                   Project ID
                                   <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
                                 </Typography>
                                 <Tooltip title="A unique 5-digit identifier for the project. This ID is used internally to reference the project and must be unique across all projects." arrow placement="top">
                                   <IconButton size="small" sx={{ p: 0.25 }}>
                                     <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                   </IconButton>
                                 </Tooltip>
                               </Box>
                               <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                 <TextField
                                  {...field}
                                  placeholder="e.g., 12345"
                                  error={!!errors.projectId}
                                  helperText={errors.projectId?.message}
                                  sx={{ flex: '1 1 200px', minWidth: 0 }}
                                  size="small"
                                  inputProps={{ inputMode: 'numeric', pattern: '\\d{5}' }}
                                />
                                 <Button
                                   variant="outlined"
                                   onClick={generateProjectId}
                                   startIcon={<RefreshIcon />}
                                   sx={{ minWidth: 'fit-content', flex: '0 0 auto' }}
                                 >
                                   Autogenerate
                                 </Button>
                               </Box>
                               {/* <FormHelperText>
                                 A unique 5-digit identifier for the project.
                               </FormHelperText> */}
                             </FormControl>
                           )}
                         />

                         {/* Project Name */}
                         <Controller
                           name="name"
                           control={control}
                           render={({ field }) => (
                             <FormControl fullWidth error={!!errors.name}>
                               <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                                 <Typography variant="subtitle1" sx={{ 
                                   display: 'flex', 
                                   alignItems: 'center' 
                                 }}>
                                   {/* <InventoryIcon sx={{ mr: 1, color: 'primary.main' }} /> */}
                                   Project Name
                                   <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
                                 </Typography>
                                 <Tooltip title="A descriptive name for the project. Only lowercase letters, numbers, and hyphens are allowed. Cannot start/end with hyphen or have consecutive hyphens." arrow placement="top">
                                   <IconButton size="small" sx={{ p: 0.25 }}>
                                     <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                   </IconButton>
                                 </Tooltip>
                               </Box>
                               <TextField
                                 {...field}
                                 placeholder="e.g., my-energy-project"
                                 error={!!errors.name}
                                 helperText={errors.name?.message || ''}
                                 size="small"
                                 onChange={e => {
                                   // Allow users to type freely, but convert to lowercase
                                   field.onChange(e.target.value.toLowerCase());
                                 }}
                                 onBlur={(e) => {
                                   // Clean up the value when user finishes typing
                                   let value = e.target.value;
                                   
                                   // Only allow alphanumeric characters and hyphens
                                   value = value.replace(/[^a-zA-Z0-9-]/g, '');
                                   
                                   // Prevent consecutive hyphens
                                   value = value.replace(/--+/g, '-');
                                   
                                   // Prevent starting with hyphen
                                   value = value.replace(/^-+/, '');
                                   
                                   // Prevent ending with hyphen
                                   value = value.replace(/-+$/, '');
                                   
                                   // Update the field with cleaned value (already lowercase from onChange)
                                   field.onChange(value);
                                 }}
                                 value={field.value || ''}
                                 inputProps={{
                                   pattern: '[a-z0-9-]*',
                                   title: 'Only lowercase letters, numbers, and hyphens are allowed'
                                 }}
                               />
                             </FormControl>
                           )}
                         />

                         {/* Project Description */}
                         <Controller
                           name="description"
                           control={control}
                           render={({ field }) => (
                             <FormControl fullWidth error={!!errors.description}>
                               <Typography variant="subtitle1" sx={{ 
                                 display: 'flex', 
                                 alignItems: 'center', 
                                 mb: 1 
                               }}>
                                 Project Description
                               </Typography>
                               <TextField
                                 {...field}
                                 multiline
                                 rows={3}
                                 placeholder="Describe your project..."
                                 error={!!errors.description}
                                 helperText={errors.description?.message || 'Optional. Provide a brief description of your project.'}
                                 size="small"
                               />
                             </FormControl>
                           )}
                         />

                         {/* Project Image URL */}
                         <Controller
                           name="projectImageUrl"
                           control={control}
                           render={({ field }) => (
                             <FormControl fullWidth error={!!errors.projectImageUrl}>
                               <Typography variant="subtitle1" sx={{ 
                                 display: 'flex', 
                                 alignItems: 'center', 
                                 mb: 1 
                               }}>
                                 Project Image URL
                               </Typography>
                               <TextField
                                 {...field}
                                 type="url"
                                 placeholder="https://example.com/image.png"
                                 error={!!errors.projectImageUrl}
                                 helperText={errors.projectImageUrl?.message || 'Optional. Recommended 2:1 aspect ratio (e.g., 400x200 pixels).'}
                               />
                               {field.value && field.value.startsWith('http') && (
                                 <Box sx={{ mt: 2, border: 1, borderColor: 'divider', borderRadius: 1, p: 1, display: 'inline-block', bgcolor: 'background.default' }}>
                                   <img
                                     src={field.value}
                                     alt="Project preview"
                                     style={{ width: 200, height: 100, objectFit: 'cover', borderRadius: 4 }}
                                   />
                                 </Box>
                               )}
                               {field.value && !field.value.startsWith('http') && (
                                 <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                   Please enter a valid URL starting with http(s) to see a preview.
                                 </Typography>
                               )}
                             </FormControl>
                           )}
                         />
                       </Box>
                     )}



                     {/* Dynamic Config Form for unmapped config registry fields */}
                     {dynamicConfigData.length > 0 && !isEditMode && (
                       <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                         {/* <Typography variant="h6" sx={{ 
                           display: 'flex', 
                           alignItems: 'center', 
                           mb: 2 
                         }}>
                           <StorageIcon sx={{ mr: 1, color: 'primary.main' }} />
                           Additional Configuration
                         </Typography> */}
                         <DynamicConfigForm
                           configRegistryData={dynamicConfigData}
                           control={control}
                           errors={errors}
                           setValue={setValue}
                           getValues={getValues}
                           projectId={getValues("projectId")}
                         />
                       </Box>
                     )}
                </Box>
              </CardContent>
              
              <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider',justifyContent :'flex-end', alignItems:'flex-end',display:'flex' }}>
              
                <BglPrimaryButton
                  type="submit"
                  disabled={isLoading}
                  sx={{ width: isEditMode ? '180px' : projectAlreadyExists? '360px' : '260px', fontSize: '14px', textTransform: 'none' }}
                  endIcon={!isEditMode ? <ArrowForwardIcon /> : undefined}
                >
                  {isLoading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Processing...
                    </>
                  ) : (
                    isEditMode ?
                    <> 
                    Save Changes <Save style={{ marginLeft: 4 }} size={16} />
                    </> : 
                    projectAlreadyExists ? 'Update Project & Continue to Step 2' : 'Next: Ingestion Configs'
                  )}
                </BglPrimaryButton>
              </Box>
            </form>
          </Card>
        </Box>
      </Box>

      {/* Create Utility Dialog */}
      <Dialog 
        open={showCreateUtilityDialog} 
        onClose={() => setShowCreateUtilityDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon sx={{ color: 'primary.main' }} />
            Create New Utility
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              value={newUtilityName}
              onChange={(e) => setNewUtilityName(e.target.value)}
              placeholder="Enter utility name..."
              error={!!createUtilityError}
              helperText={createUtilityError || "Enter the name for the new utility"}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleCreateUtilitySubmit();
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowCreateUtilityDialog(false)}
            disabled={isCreatingUtility}
          >
            Cancel
          </Button>
          <BglPrimaryButton
            onClick={handleCreateUtilitySubmit}
            disabled={isCreatingUtility || !newUtilityName.trim()}
            startIcon={isCreatingUtility ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {isCreatingUtility ? 'Creating...' : 'Create Utility'}
          </BglPrimaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 